/**
 * Verify Email Change Page
 * Handles email change verification from the email link
 */

import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as authService from "@services/auth";
import { clearAuthStorage, setStoredTenantId } from "@utils/storage";

type VerificationState = "verifying" | "success" | "error";

export function VerifyEmailChangePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const tenantId = searchParams.get("tenant");

  // Initialize state based on token presence to avoid setState in effect
  const [state, setState] = useState<VerificationState>(() =>
    token ? "verifying" : "error",
  );
  const [error, setError] = useState<string | null>(() =>
    token ? null : "No verification token provided",
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    const verifyEmail = async () => {
      try {
        // If tenant ID is in the URL, set it before making the API call
        // This ensures the correct X-Tenant-ID header is sent even on a new device/browser
        if (tenantId) {
          setStoredTenantId(tenantId);
        }

        await authService.verifyEmailChange(token);
        setState("success");
        // Clear auth storage since all sessions are invalidated
        clearAuthStorage();
      } catch (err) {
        setState("error");
        const error = err as { detail?: string; message?: string };
        setError(
          error.detail || error.message || "Failed to verify email change",
        );
      }
    };

    verifyEmail();
  }, [token, tenantId]);

  // Verifying state
  if (state === "verifying") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4 py-12">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <svg
              className="w-8 h-8 text-white animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Verifying Email Change
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we verify your new email address...
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (state === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Email Changed Successfully
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your email address has been updated. For security, all your
              sessions have been logged out. Please sign in with your new email
              address.
            </p>
            <Link
              to="/login"
              className="w-full flex justify-center py-3 px-4 bg-gradient-to-r from-primary-500 to-purple-600
                       text-white font-medium rounded-lg shadow-lg
                       hover:from-primary-600 hover:to-purple-700
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                       transition-all duration-200"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Verification Failed
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "The verification link is invalid or has expired."}
          </p>
          <div className="space-y-3">
            <Link
              to="/profile/settings"
              className="w-full flex justify-center py-3 px-4 bg-gradient-to-r from-primary-500 to-purple-600
                       text-white font-medium rounded-lg shadow-lg
                       hover:from-primary-600 hover:to-purple-700
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                       transition-all duration-200"
            >
              Try again from Settings
            </Link>
            <Link
              to="/"
              className="w-full flex justify-center py-3 px-4
                       text-gray-700 dark:text-gray-300 font-medium rounded-lg
                       hover:bg-gray-100 dark:hover:bg-gray-700
                       transition-all duration-200"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
